import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { usePersistedState, writePersisted } from '@/hooks/usePersistedState'
import { paramsPatch } from '@/lib/urlParams'
import { readNewRunLink, readRunsLink, type RunStatusFilter } from '@/lib/runsRegister'

export const RUNS_STATUS_KEY = 'kf49.runs.statusFilter'
export const RUNS_SEARCH_KEY = 'kf49.runs.searchQuery'

/**
 * The Runs register's status chip, search box and history window — and what a
 * link into the register does to them (`?status=` / `?history=all`, e.g. Home's
 * "Active runs" tile, F16 UF-05, which sends `?status=ACTIVE,TIPPING_COMPLETE`
 * and lands on the "Active or tipping stopped" chip — what the tile counts).
 *
 * The status and the search are saved PER DEVICE (persisted state), so a link
 * WRITES into them: arriving on `?status=…` replaces the device's earlier
 * chip, and clears its saved search — a leftover term would narrow the list
 * by something the tile never applied, or empty it. The window lifts for this
 * visit only (it is plain state).
 *
 * The link is applied BEFORE the saved values are read — the storage is
 * seeded in the first state initialiser, and the link's values are also the
 * fallbacks — so the very first register read is already the link's. Applied
 * in an effect instead, the first render fired one read with the old filter.
 * The effect then only drops the link from the address (and applies a link
 * that arrives while the page is already mounted).
 */
export function useRunsLinkFilters() {
  const [params, setParams] = useSearchParams()
  const [arrival] = useState(() => {
    const link = readRunsLink(params)
    if (link) {
      if (link.status) writePersisted(RUNS_STATUS_KEY, link.status)
      writePersisted(RUNS_SEARCH_KEY, '')
    }
    return link
  })
  const [statusFilter, setStatusFilter] = usePersistedState<RunStatusFilter>(RUNS_STATUS_KEY, arrival?.status ?? 'ALL')
  const [searchQuery, setSearchQuery] = usePersistedState<string>(RUNS_SEARCH_KEY, '')
  // M12 — the list defaults to the last 90 days; "Show all" lifts it.
  const [showAllHistory, setShowAllHistory] = useState(arrival?.allHistory ?? false)

  useEffect(() => {
    const link = readRunsLink(params)
    if (!link) return
    if (link.status) setStatusFilter(link.status)
    if (link.allHistory) setShowAllHistory(true)
    setSearchQuery('')
    setParams(prev => paramsPatch(prev, { status: null, history: null }), { replace: true })
  }, [params]) // eslint-disable-line react-hooks/exhaustive-deps

  return { statusFilter, setStatusFilter, searchQuery, setSearchQuery, showAllHistory, setShowAllHistory }
}

/**
 * F02 UF-03: the `?new=1&delivery=<uuid>` link that opens New Run with that
 * delivery ticked (F01's "Start a run" uses newRunHref). Read once on arrival
 * and dropped from the address so a refresh or Back does not re-open it. A
 * role that cannot start a run is left on the register. `nonce` changes per
 * arrival so a second link while mounted re-applies.
 */
export function useNewRunLink(canStartRun: boolean, roleKnown: boolean) {
  const [params, setParams] = useSearchParams()
  const [state, setState] = useState<{ openNew: boolean; deliveryId: string | null; growerId: string | null; variety: string | null; nonce: number }>(
    { openNew: false, deliveryId: null, growerId: null, variety: null, nonce: 0 })
  useEffect(() => {
    const link = readNewRunLink(params)
    if (!link || !roleKnown) return   // wait for the role: a link must not be dropped while it loads
    if (canStartRun) setState(s => ({ openNew: true, deliveryId: link.deliveryId, growerId: link.growerId, variety: link.variety, nonce: s.nonce + 1 }))
    setParams(prev => paramsPatch(prev, { new: null, delivery: null, grower: null, variety: null }), { replace: true })
  }, [params, canStartRun, roleKnown]) // eslint-disable-line react-hooks/exhaustive-deps
  return state
}
